/**
 * LISTAS DE PRECIOS PAGE - Manage price lists and exam prices
 * CRUD for ListaPrecio with nested PrecioExamen management via Sheet.
 */

import { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DollarSign,
  Plus,
  MoreHorizontal,
  Pencil,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  Trash2,
  Search,
  X,
  Eye,
} from "lucide-react";
import { ResponsiveTable, useIsMobile } from "@/components/ui/responsive-table";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import {
  DataTableColumnHeader,
  DataTablePagination,
  DataTableViewOptions,
} from "@/components/ui/data-table";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

import { useApiError } from "@/hooks/use-api-error";
import { useServerDataTable } from "@/hooks/use-server-data-table";
import {
  useListaPrecio,
  useActivateListaPrecio,
  useDeactivateListaPrecio,
  useDeleteListaPrecio,
  type ListaPrecio,
} from "@/hooks/use-facturacion";
import { getListasPrecios, TIPOS_LISTA_PRECIO } from "@/hooks/use-facturacion";

import { ListaPrecioFormDialog } from "../components/lista-precio-form-dialog";
import { PreciosExamenSheet } from "../components/precios-examen-sheet";

// Tipo badge color mapping
function TipoBadge({ tipo }: { tipo: ListaPrecio["tipo"] }) {
  const { t } = useTranslation();
  const variants: Record<string, string> = {
    PUBLICO: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30",
    FERIADO: "bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30",
    ALIANZA: "bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/30",
    CORPORATIVO: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  };
  return (
    <Badge className={variants[tipo] || ""}>{t(`facturacion.listasPrecios.tipos.${tipo}`)}</Badge>
  );
}

export default function ListasPreciosPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const isMobile = useIsMobile();

  // Dialog state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isPreciosSheetOpen, setIsPreciosSheetOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectedForAction, setSelectedForAction] = useState<ListaPrecio | null>(null);
  const [selectedForPrecios, setSelectedForPrecios] = useState<ListaPrecio | null>(null);
  const [confirmAction, setConfirmAction] = useState<"activate" | "deactivate" | "delete" | null>(
    null
  );

  // Fetch full data for editing
  const { data: selectedListaPrecio } = useListaPrecio(selectedId || 0, { enabled: !!selectedId });
  const isFormReady = selectedId === null || !!selectedListaPrecio;

  // Mutations
  const activateMutation = useActivateListaPrecio({
    onSuccess: () => {
      toast.success(t("facturacion.listasPrecios.toast.activated"));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deactivateMutation = useDeactivateListaPrecio({
    onSuccess: () => {
      toast.warning(t("facturacion.listasPrecios.toast.deactivated"));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteMutation = useDeleteListaPrecio({
    onSuccess: () => {
      toast.error(t("facturacion.listasPrecios.toast.deleted"));
      refetch();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  // Handlers
  const handleEdit = useCallback((item: ListaPrecio) => {
    setSelectedId(item.id);
    setIsFormOpen(true);
  }, []);

  const handleViewPrecios = useCallback((item: ListaPrecio) => {
    setSelectedForPrecios(item);
    setIsPreciosSheetOpen(true);
  }, []);

  const handleToggleStatus = useCallback((item: ListaPrecio) => {
    setSelectedForAction(item);
    setConfirmAction(item.is_active ? "deactivate" : "activate");
    setIsConfirmOpen(true);
  }, []);

  const handleDelete = useCallback((item: ListaPrecio) => {
    setSelectedForAction(item);
    setConfirmAction("delete");
    setIsConfirmOpen(true);
  }, []);

  const handleCreate = () => {
    setSelectedId(null);
    setIsFormOpen(true);
  };

  const handleConfirmAction = () => {
    if (!selectedForAction || !confirmAction) return;
    if (confirmAction === "activate") activateMutation.mutate(selectedForAction.id);
    else if (confirmAction === "deactivate") deactivateMutation.mutate(selectedForAction.id);
    else if (confirmAction === "delete") deleteMutation.mutate(selectedForAction.id);
    setIsConfirmOpen(false);
    setSelectedForAction(null);
    setConfirmAction(null);
  };

  const handleFormSuccess = (isNew: boolean) => {
    setIsFormOpen(false);
    toast.success(
      isNew
        ? t("facturacion.listasPrecios.toast.created")
        : t("facturacion.listasPrecios.toast.updated")
    );
    setSelectedId(null);
    refetch();
  };

  // Columns
  const columns: ColumnDef<ListaPrecio>[] = useMemo(
    () => [
      {
        accessorKey: "nombre",
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t("facturacion.listasPrecios.columns.nombre")}
          />
        ),
        cell: ({ row }) => (
          <button
            className="font-medium text-primary hover:underline text-left"
            onClick={() => handleViewPrecios(row.original)}
          >
            {row.original.nombre}
          </button>
        ),
      },
      {
        accessorKey: "tipo",
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title={t("facturacion.listasPrecios.columns.tipo")}
          />
        ),
        cell: ({ row }) => <TipoBadge tipo={row.original.tipo} />,
      },
      {
        accessorKey: "descripcion",
        header: () => t("facturacion.listasPrecios.columns.descripcion"),
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground truncate max-w-[200px] block">
            {row.original.descripcion || "—"}
          </span>
        ),
        enableSorting: false,
      },
      {
        id: "estado",
        header: () => t("facturacion.listasPrecios.columns.estado"),
        cell: ({ row }) => (
          <Badge variant={row.original.is_active ? "default" : "secondary"}>
            {row.original.is_active ? t("common.active") : t("common.inactive")}
          </Badge>
        ),
        enableSorting: false,
      },
      {
        id: "actions",
        header: () => t("common.actions"),
        cell: ({ row }) => {
          const item = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleViewPrecios(item)}>
                  <Eye className="mr-2 h-4 w-4" />
                  {t("facturacion.listasPrecios.viewPrecios")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleEdit(item)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  {t("common.edit")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleToggleStatus(item)}>
                  {item.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!item.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(item)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        enableSorting: false,
      },
    ],
    [t, handleEdit, handleViewPrecios, handleToggleStatus, handleDelete]
  );

  // Server-side data table
  const {
    table,
    isLoading,
    error,
    totalCount,
    refetch,
    searchTerm,
    setSearchTerm,
    filters,
    setFilter,
    clearFilters,
    hasActiveFilters,
  } = useServerDataTable({
    columns,
    fetchFn: getListasPrecios,
    queryKey: "listasPrecios",
    storageKey: "listas_precios_page_size",
    defaultPageSize: 20,
    filters: [
      { key: "is_active", defaultValue: "all", urlParam: "status" },
      { key: "tipo", defaultValue: "all", urlParam: "tipo" },
    ],
  });

  const columnLabels = {
    nombre: t("facturacion.listasPrecios.columns.nombre"),
    tipo: t("facturacion.listasPrecios.columns.tipo"),
  };

  // Mobile card
  const renderMobileCard = useCallback(
    (item: ListaPrecio) => (
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={item.is_active ? "default" : "secondary"} className="text-xs">
              {item.is_active ? t("common.active") : t("common.inactive")}
            </Badge>
            <TipoBadge tipo={item.tipo} />
          </div>
          <p className="font-medium mt-1">{item.nombre}</p>
          {item.descripcion && (
            <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{item.descripcion}</p>
          )}
          <div className="flex gap-1 mt-3">
            <Button variant="outline" size="sm" onClick={() => handleViewPrecios(item)}>
              <Eye className="h-3 w-3 mr-1" />
              {t("facturacion.listasPrecios.viewPrecios")}
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleEdit(item)}>
              <Pencil className="h-3 w-3 mr-1" />
              {t("common.edit")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreHorizontal className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleToggleStatus(item)}>
                  {item.is_active ? (
                    <>
                      <XCircle className="mr-2 h-4 w-4" />
                      {t("common.deactivate")}
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 h-4 w-4" />
                      {t("common.activate")}
                    </>
                  )}
                </DropdownMenuItem>
                {!item.is_active && (
                  <DropdownMenuItem
                    onClick={() => handleDelete(item)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {t("common.delete")}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    ),
    [t, handleEdit, handleViewPrecios, handleToggleStatus, handleDelete]
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <DollarSign className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">
              {t("facturacion.listasPrecios.title")}
            </h1>
            <p className="text-sm text-muted-foreground hidden sm:block">
              {t("facturacion.listasPrecios.description")}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size={isMobile ? "sm" : "default"}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? "animate-spin" : ""} ${isMobile ? "" : "mr-2"}`}
            />
            {!isMobile && t("common.refresh")}
          </Button>
          <Button size={isMobile ? "sm" : "default"} onClick={handleCreate}>
            <Plus className={`h-4 w-4 ${isMobile ? "" : "mr-2"}`} />
            {isMobile ? t("common.new") : t("facturacion.listasPrecios.newLista")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 sm:pt-6">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t("facturacion.listasPrecios.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filters.tipo} onValueChange={(value) => setFilter("tipo", value)}>
                <SelectTrigger className="w-full sm:w-[160px]">
                  <SelectValue placeholder={t("facturacion.listasPrecios.filterByTipo")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("facturacion.listasPrecios.allTipos")}</SelectItem>
                  {TIPOS_LISTA_PRECIO.map((tp) => (
                    <SelectItem key={tp.value} value={tp.value}>
                      {t(`facturacion.listasPrecios.tipos.${tp.value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={filters.is_active}
                onValueChange={(value) => setFilter("is_active", value)}
              >
                <SelectTrigger className="w-full sm:w-[140px]">
                  <SelectValue placeholder={t("facturacion.listasPrecios.filterByStatus")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("facturacion.listasPrecios.allStatus")}</SelectItem>
                  <SelectItem value="true">{t("common.active")}</SelectItem>
                  <SelectItem value="false">{t("common.inactive")}</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="secondary" size="icon" onClick={clearFilters} className="shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-md bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardDescription>
              {t("facturacion.listasPrecios.showingResults", { count: totalCount })}
            </CardDescription>
            {!isMobile && <DataTableViewOptions table={table} columnLabels={columnLabels} />}
          </div>
        </CardHeader>
        <CardContent>
          <ResponsiveTable
            table={table}
            columns={columns}
            isLoading={isLoading}
            emptyMessage={t("facturacion.listasPrecios.noResults")}
            hideOnTablet={["descripcion"]}
            renderMobileCard={renderMobileCard}
          />
          <DataTablePagination table={table} totalRows={totalCount} />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <ListaPrecioFormDialog
        open={isFormOpen && isFormReady}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) setSelectedId(null);
        }}
        listaPrecio={selectedListaPrecio || null}
        onSuccess={handleFormSuccess}
      />

      <PreciosExamenSheet
        open={isPreciosSheetOpen}
        onOpenChange={setIsPreciosSheetOpen}
        listaPrecio={selectedForPrecios}
      />

      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title={
          confirmAction === "activate"
            ? t("facturacion.listasPrecios.confirmActivate")
            : confirmAction === "delete"
              ? t("facturacion.listasPrecios.confirmDelete")
              : t("facturacion.listasPrecios.confirmDeactivate")
        }
        description={
          confirmAction === "activate"
            ? t("facturacion.listasPrecios.confirmActivateDesc")
            : confirmAction === "delete"
              ? t("facturacion.listasPrecios.confirmDeleteDesc")
              : t("facturacion.listasPrecios.confirmDeactivateDesc")
        }
        onConfirm={handleConfirmAction}
        variant={
          confirmAction === "deactivate" || confirmAction === "delete" ? "destructive" : "default"
        }
      />
    </div>
  );
}
